// Thin wrapper around @capacitor-community/bluetooth-le.
// BLE radio only works inside the Capacitor native shell.
// In Lovable web preview we expose a "simulate" mode so the UI is
// still testable: nearby peers are read from `localStorage` instead.
import { Capacitor } from "@capacitor/core";

export interface NearbyPeer {
  device_id: string;
  user_id: string;
  display_name: string;
  cashtag?: string | null;
  rssi?: number;
}

// Cash Isle BLE service UUID (random app-specific UUID)
export const CASH_ISLE_BLE_SERVICE = "c45f1571-cf15-4e1e-95e4-cf15ea5e4001";

const isNative = () => Capacitor.isNativePlatform();

export async function isBleAvailable(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { BleClient } = await import("@capacitor-community/bluetooth-le");
    await BleClient.initialize({ androidNeverForLocation: true });
    return true;
  } catch {
    return false;
  }
}

/** Scan briefly for nearby Cash Isle peers. */
export async function scanNearby(timeoutMs = 5000): Promise<NearbyPeer[]> {
  if (!isNative()) {
    // Simulator fallback for web preview
    try {
      return JSON.parse(localStorage.getItem("cashisle_ble_sim_peers") ?? "[]");
    } catch {
      return [];
    }
  }
  const { BleClient } = await import("@capacitor-community/bluetooth-le");
  await BleClient.initialize({ androidNeverForLocation: true });
  const peers: Record<string, NearbyPeer> = {};
  await BleClient.requestLEScan(
    { services: [CASH_ISLE_BLE_SERVICE] },
    (res) => {
      const name = res.localName || res.device?.name || "Cash Isle user";
      const id = res.device?.deviceId || name;
      // Manufacturer/service data carries user_id + cashtag in real impl
      peers[id] = {
        device_id: id,
        user_id: id,
        display_name: name,
        rssi: res.rssi,
      };
    },
  );
  await new Promise((r) => setTimeout(r, timeoutMs));
  await BleClient.stopLEScan();
  return Object.values(peers);
}

/** Stub: in real BLE, the receiver advertises and the sender writes to a characteristic. */
export async function sendVoucherToPeer(peer: NearbyPeer, voucherJson: string): Promise<void> {
  if (!isNative()) {
    // Web sim: drop into a peer-specific bucket the receiver page polls
    const key = `cashisle_ble_inbox_${peer.user_id}`;
    const existing = JSON.parse(localStorage.getItem(key) ?? "[]");
    existing.push(voucherJson);
    localStorage.setItem(key, JSON.stringify(existing));
    return;
  }
  // Real BLE GATT write would go here. Left as a TODO for native build:
  // const { BleClient } = await import("@capacitor-community/bluetooth-le");
  // await BleClient.connect(peer.device_id);
  // await BleClient.write(peer.device_id, CASH_ISLE_BLE_SERVICE, "<char-uuid>", new DataView(new TextEncoder().encode(voucherJson).buffer));
  throw new Error("BLE GATT transfer not yet implemented for native — see src/lib/ble/transport.ts");
}

/** Web-sim helper: register this user as a discoverable peer locally. */
export function simAdvertise(peer: NearbyPeer) {
  if (isNative()) return;
  const list: NearbyPeer[] = JSON.parse(localStorage.getItem("cashisle_ble_sim_peers") ?? "[]");
  const filtered = list.filter((p) => p.user_id !== peer.user_id);
  filtered.push(peer);
  localStorage.setItem("cashisle_ble_sim_peers", JSON.stringify(filtered));
}

/** Web-sim helper: drain inbox for a given user. */
export function simDrainInbox(userId: string): string[] {
  if (isNative()) return [];
  const key = `cashisle_ble_inbox_${userId}`;
  const list = JSON.parse(localStorage.getItem(key) ?? "[]");
  localStorage.setItem(key, "[]");
  return list;
}
